import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  notificationPanelTitle,
  resolveNotificationModuleParam,
} from "@/config/notificationModuleScope";
import { useActiveModule } from "@/hooks/useActiveModule";
import { useNotificationInbox } from "@/hooks/useNotificationInbox";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  formatNotificationTime,
  isNotificationUnread,
} from "@/utils/notificationUi";
import { showApiError } from "@/utils/apiError";

export function NotificationDropdown() {
  const location = useLocation();
  const activeModule = useActiveModule();
  const moduleParam = resolveNotificationModuleParam(
    location.pathname,
    activeModule,
  );

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelTitle = notificationPanelTitle(moduleParam);
  const { items, unread, loading, load, markRead, markAllRead } =
    useNotificationInbox({ module: moduleParam, size: 10 });

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleMarkRead = async (id: number) => {
    try {
      await markRead(id);
    } catch (err) {
      showApiError(err, "Failed to mark notification read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
    } catch (err) {
      showApiError(err, "Failed to mark all read");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <Tooltip content={panelTitle}>
        <button
          type="button"
          className="icon-btn icon-btn-md relative"
          onClick={() => {
            setOpen((value) => !value);
            if (!open) void load();
          }}
          aria-label={panelTitle}
        >
          <NotificationsIcon fontSize="small" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </Tooltip>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
            <span className="text-sm font-semibold">{panelTitle}</span>
            {unread > 0 && (
              <button
                type="button"
                className="text-xs text-primary-600"
                onClick={() => void handleMarkAllRead()}
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && <p className="p-3 text-sm text-slate-500">Loading…</p>}
            {!loading && items.length === 0 && (
              <p className="p-3 text-sm text-slate-500">
                {moduleParam
                  ? "No notifications for this module"
                  : "No notifications"}
              </p>
            )}
            {items.map((notification) => (
              <button
                key={notification.id}
                type="button"
                className={`block w-full border-b border-slate-100 px-3 py-2 text-left hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800 ${
                  isNotificationUnread(notification)
                    ? "bg-primary-50/50 dark:bg-primary-950/30"
                    : ""
                }`}
                onClick={() => {
                  if (isNotificationUnread(notification)) {
                    void handleMarkRead(notification.id);
                  }
                }}
              >
                <p className="text-sm font-medium">{notification.title}</p>
                <p className="text-xs text-slate-500 line-clamp-2">
                  {notification.message}
                </p>
                <p className="mt-1 text-[10px] text-slate-400">
                  {formatNotificationTime(notification.createdAt)}
                </p>
              </button>
            ))}
          </div>
          <div className="border-t border-slate-200 px-3 py-2 dark:border-slate-700">
            <Link
              to="/notifications"
              className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
              onClick={() => setOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
